// resolveApiError.ts turns a rejected API call into copy a customer can act on.
//
// The problem it exists to solve (BUG-449): catch blocks across the app interpolate
// the caught value straight into the toast — `message: \`No pudimos guardar ${error}\``
// — and JS coerces an AxiosError to "AxiosError: Request failed with status code 422".
// The backend had already said exactly what was wrong; the interpolation threw it away.
// A real report: a customer editing a lead name saw only "request fail with status
// code 422" and retried the same value four times before opening a ticket.
//
// The `t` passed in should be bound to the `errors/errors` namespace, which owns this
// copy in every locale. Callers obtain it via useTranslation('errors/errors').
//
// Every path is guarded so this helper can only ever return human copy: never a raw
// axios string, and never a bare i18n key. That second guard is not theoretical —
// RET-1977 shipped "payment-method-updated" to customers because the key was looked
// up in a namespace that did not hold it, and production pins the language to Spanish
// (fallbackLng: 'es'), so there was nothing to fall back to.

import type { TFunction } from 'i18next';

export type tApiErrorDetail = {
  /** HTTP status, or undefined when the request never got a response. */
  status?: number;
  /** Field-keyed validation messages, e.g. { full_name: 'The full name field must…' }. */
  fieldErrors: Record<string, string>;
  /** A plain-string `message` from the body, when the body used one. */
  serverMessage?: string;
};

/** Matches the axios message we must never show, in either casing seen in the wild. */
const AXIOS_STATUS_MESSAGE = /request failed with status code/i;

/**
 * Laravel's `max` copy, e.g. "must not be greater than 255 characters". Coupled
 * to that exact English phrasing on purpose — this is a best-effort upgrade, not
 * a contract. If leads-service ever localizes or rewords the message, the regex
 * stops matching and `resolveFieldError` returns `undefined` for that field,
 * which is a designed miss: the caller falls through to the generic validation
 * sentence (safe, if less specific), never a blank or half-built message.
 */
const MAX_LENGTH_MESSAGE = /greater than (\d+) characters/i;

const STATUS_KEYS: Record<number, string> = {
  400: 'apiErrors.validation',
  401: 'apiErrors.unauthorized',
  403: 'apiErrors.forbidden',
  404: 'apiErrors.notFound',
  409: 'apiErrors.conflict',
  422: 'apiErrors.validation',
  429: 'apiErrors.rateLimited',
};

// Spanish last-resort copy, used only when the namespace failed to supply the key.
// Production is Spanish-locked, so these read as the real strings, not placeholders.
const DEFAULTS: Record<string, string> = {
  'apiErrors.validation':
    'Revisa la información: hay un dato inválido o demasiado largo.',
  'apiErrors.unauthorized': 'Tu sesión expiró. Vuelve a iniciar sesión.',
  'apiErrors.forbidden': 'No tienes permisos para hacer esto.',
  'apiErrors.notFound': 'No encontramos lo que buscabas.',
  'apiErrors.conflict': 'Ese dato ya existe. Revisa la información.',
  'apiErrors.rateLimited':
    'Demasiados intentos. Espera unos minutos e intenta de nuevo.',
  'apiErrors.server': 'Tuvimos un problema de nuestro lado. Intenta de nuevo.',
  'apiErrors.network':
    'No pudimos conectarnos. Revisa tu conexión e intenta de nuevo.',
  'apiErrors.generic': 'Algo salió mal. Intenta de nuevo.',
  'apiErrors.tooLong':
    'El campo {{field}} supera el límite de {{max}} caracteres.',
  'apiErrors.context.saveTag': 'No pudimos guardar la etiqueta.',
  'apiErrors.context.deleteTag': 'No pudimos eliminar la etiqueta.',
  // Only the one field label a caller currently needs this fallback for
  // (ChatInfoAttributes.schema.ts's client-side length validation). Add an
  // entry here before routing a new field through resolveCopy for the same
  // reason — an unlisted key falls back to apiErrors.generic, which reads as
  // nonsense once interpolated into "El campo <generic sentence> supera...".
  'apiErrors.fields.full_name': 'Nombre',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Looks up localized copy, falling back to Spanish when the key is missing.
 * react-i18next echoes the key on a miss, so an unguarded `t()` can return
 * "apiErrors.generic" as if it were a sentence.
 */
function copy(
  t: TFunction,
  options: { key: string; vars?: Record<string, string | number> },
): string {
  const { key, vars: opts } = options;
  const resolved = t(key, opts) as unknown as string;
  if (typeof resolved === 'string' && resolved !== key && resolved.length > 0) {
    return resolved;
  }
  const fallback = DEFAULTS[key] ?? DEFAULTS['apiErrors.generic'];
  return fallback.replace(/\{\{(\w+)\}\}/g, (_match, name: string) =>
    opts && opts[name] !== undefined ? String(opts[name]) : `{{${name}}}`,
  );
}

/**
 * The same missing-key guard `resolveApiErrorMessage` uses, for callers that
 * compose their own sentence around it (e.g. "<context>. <reason>") or need a
 * key outside this module's own resolution flow (e.g. a Yup validation message).
 * Calling `t()` directly there would reintroduce the bare-key leak this module
 * exists to prevent, since locale bundles are fetched over HTTP at runtime and a
 * failed fetch leaves every key unresolved.
 */
export function resolveCopy(
  t: TFunction,
  key: string,
  vars?: Record<string, string | number>,
): string {
  return copy(t, { key, vars });
}

/**
 * Pulls the useful parts out of a rejected request without assuming axios.
 * Duck-types on `response.status` so any similar client works, and so the
 * shape stays trivially constructible in tests.
 *
 * Covers `message: {<field>: <reason>}` (leads-service) and `message: {<field>:
 * [<reason>, ...]}` (Laravel's own default validator envelope). It does NOT read
 * bff-web's `errors: ["validator/<field>/<tag>"]` array — a caller against a
 * bff-web endpoint still gets safe generic copy, but not the field-specific
 * message that shape could support. See
 * docs/solutions/integration-issues/fe-bff-validation-limit-drift-silent-toast.md.
 */
export function extractApiError(err: unknown): tApiErrorDetail {
  const detail: tApiErrorDetail = {
    status: undefined,
    fieldErrors: {},
    serverMessage: undefined,
  };
  if (!isRecord(err)) return detail;

  const response = isRecord(err.response) ? err.response : undefined;
  if (response && typeof response.status === 'number') {
    detail.status = response.status;
  }

  const data = response && isRecord(response.data) ? response.data : undefined;
  if (!data) return detail;

  const message = data.message;
  if (typeof message === 'string') {
    detail.serverMessage = message;
  } else if (isRecord(message)) {
    // Two shapes share this position, both keyed by field name:
    //   leads-service (app/Http/Requests/FormRequest.php::failedValidation):
    //     { <field>: '<reason>' } — one string per field.
    //   Laravel's own default validator envelope:
    //     { <field>: ['<reason>', ...] } — an array per field; take the first
    //     string entry, since that is the one a caller renders as *the* reason.
    // Anything else (a nested object, a number) is a shape we should not render.
    for (const [field, reason] of Object.entries(message)) {
      if (typeof reason === 'string') {
        detail.fieldErrors[field] = reason;
      } else if (Array.isArray(reason)) {
        const first = reason.find((entry): entry is string => typeof entry === 'string');
        if (first !== undefined) detail.fieldErrors[field] = first;
      }
    }
  }

  return detail;
}

/**
 * True when the value looks like a request that never came back (offline, DNS,
 * timeout). A plain `new Error(...)` is not one of these — it gets generic copy.
 */
function isRequestError(err: unknown): boolean {
  if (!isRecord(err)) return false;
  return err.isAxiosError === true || 'request' in err;
}

/**
 * Resolution order:
 *   1. a deliberately thrown string → shown as-is (callers already localize these)
 *   2. 422/400 naming a field we have a label for, with a parseable length limit
 *      → "El campo Nombre supera el límite de 255 caracteres."
 *   3. any mapped status → its localized copy
 *   4. 5xx → server copy
 *   5. no response on a request-shaped error → network copy
 *   6. an unmapped status carrying a human server message → that message
 *   7. anything else → generic copy
 *
 * Never returns a raw axios string and never returns a bare i18n key.
 */
export function resolveApiErrorMessage(err: unknown, t: TFunction): string {
  // Callers such as SignUp throw localized strings on purpose; keep showing them.
  if (typeof err === 'string') return err;

  const { status, fieldErrors, serverMessage } = extractApiError(err);

  if (status === 422 || status === 400) {
    const specific = resolveFieldError(fieldErrors, t);
    if (specific) return specific;
  }

  const statusKey = status === undefined ? undefined : STATUS_KEYS[status];
  if (statusKey) return copy(t, { key: statusKey });

  if (status !== undefined && status >= 500) {
    return copy(t, { key: 'apiErrors.server' });
  }

  if (status === undefined && isRequestError(err)) {
    return copy(t, { key: 'apiErrors.network' });
  }

  // An unmapped status (e.g. 418) may still carry something worth showing — but only
  // if it is real copy, not the upstream axios string a gateway echoed into `message`.
  if (serverMessage && !AXIOS_STATUS_MESSAGE.test(serverMessage)) {
    return serverMessage;
  }

  return copy(t, { key: 'apiErrors.generic' });
}

/**
 * Builds field-specific copy when we can name the field in the user's language AND
 * read the limit out of the backend message. Without a label the field key alone
 * ("full_name") is worse than the generic sentence, so we fall through instead.
 */
function resolveFieldError(
  fieldErrors: Record<string, string>,
  t: TFunction,
): string | undefined {
  // leads-service reports every failing field in one map. Reading only the first
  // entry would discard a usable message whenever the unusable one sorted first
  // (e.g. a malformed email alongside an over-long name).
  for (const [field, reason] of Object.entries(fieldErrors)) {
    const labelKey = `apiErrors.fields.${field}`;
    const label = t(labelKey) as unknown as string;
    if (typeof label !== 'string' || label === labelKey) continue;

    const max = reason.match(MAX_LENGTH_MESSAGE)?.[1];
    if (!max) continue;

    return copy(t, { key: 'apiErrors.tooLong', vars: { field: label, max } });
  }
  return undefined;
}
